import Component from 'flarum/common/Component';
import Markdown from './Markdown';

/**
 * Markdown 编辑器：上下结构，上方编辑源码，下方实时渲染预览。
 *
 * 用法：
 *   <TaroMarkdownEditor
 *     value={this.myContent}
 *     oninput={(text) => { this.myContent = text; m.redraw(); }}
 *     disabled={this.loading['something']}
 *     submitLabel="发布解读"
 *     loading={this.loading['interpretation']}
 *     onsubmit={() => this.submitMyContent()}
 *     oncancel={() => { this.showEditor = false; }}
 *   />
 */
export default class TaroMarkdownEditor extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this._textarea = null;
    this._showHelp = false;
  }

  get value() {
    return this.attrs.value || '';
  }

  get disabled() {
    return this.attrs.disabled || false;
  }

  get loading() {
    return this.attrs.loading || false;
  }

  get submitLabel() {
    return this.attrs.submitLabel || '提交';
  }

  get hasContent() {
    return this.value.trim().length > 0;
  }

  /** 在 Markdown 渲染后，为容器注入样式类并增强表格 */
  _afterRender(dom) {
    if (!dom) return;
    // 给预览容器加一个专属 class，方便写样式
    dom.classList.add('TaroMdPreview-rendered');
    // 为表格包裹容器以便横向滚动
    dom.querySelectorAll('table').forEach((tbl) => {
      if (!tbl.parentElement.classList.contains('TaroMd-table-wrap')) {
        const wrap = document.createElement('div');
        wrap.className = 'TaroMd-table-wrap';
        wrap.style.cssText = 'overflow-x:auto;margin:8px 0;';
        tbl.parentNode.insertBefore(wrap, tbl);
        wrap.appendChild(tbl);
      }
    });
  }

  view() {
    const helpStyle = {
      position: 'absolute',
      top: '100%',
      left: '0',
      zIndex: 100,
      width: '520px',
      maxHeight: '360px',
      overflowY: 'auto',
      background: '#fff',
      border: '1px solid #d2d6dc',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      padding: '14px 16px',
      fontSize: '13px',
      lineHeight: '1.7',
      color: '#333',
    };

    return (
      <div className="TaroMarkdownEditor" style="border:1px solid #d2d6dc;border-radius:8px;background:#fff;overflow:visible;">
        {/* 工具栏 */}
        <div className="TaroMarkdownEditor-toolbar" style="display:flex;align-items:center;gap:4px;padding:6px 8px;background:#f5f7fa;border-bottom:1px solid #e8ecf0;flex-wrap:wrap;position:relative;">
          <button type="button" className="TaroMdBtn" title="粗体：**粗体文字** 或 Ctrl+B" onclick={() => this.insertMdWrap('**', '**', '粗体文字')} style={this.mdBtnStyle}>
            <b>B</b>
          </button>
          <button type="button" className="TaroMdBtn" title="斜体：*斜体文字* 或 Ctrl+I" onclick={() => this.insertMdWrap('*', '*', '斜体文字')} style={this.mdBtnStyle}>
            <i>I</i>
          </button>
          <button type="button" className="TaroMdBtn" title="行内代码：`code`" onclick={() => this.insertMdWrap('`', '`', 'code')} style={this.mdBtnStyle}>
            <code style="font-size:12px;">&lt;/&gt;</code>
          </button>
          <span style="width:1px;height:18px;background:#d2d6dc;margin:0 4px;"></span>
          <button type="button" className="TaroMdBtn" title="二级标题：## 标题文字" onclick={() => this.insertLinePrefix('## ')} style={this.mdBtnStyle}>
            H2
          </button>
          <button type="button" className="TaroMdBtn" title="三级标题：### 标题文字" onclick={() => this.insertLinePrefix('### ')} style={this.mdBtnStyle}>
            H3
          </button>
          <button type="button" className="TaroMdBtn" title="引用：> 引用文字" onclick={() => this.insertLinePrefix('> ')} style={this.mdBtnStyle}>
            <i className="fas fa-quote-right"></i>
          </button>
          <button type="button" className="TaroMdBtn" title="无序列表：- 列表项" onclick={() => this.insertLinePrefix('- ')} style={this.mdBtnStyle}>
            <i className="fas fa-list-ul"></i>
          </button>
          <button type="button" className="TaroMdBtn" title="有序列表：1. 列表项" onclick={() => this.insertLinePrefix('1. ')} style={this.mdBtnStyle}>
            <i className="fas fa-list-ol"></i>
          </button>
          <span style="width:1px;height:18px;background:#d2d6dc;margin:0 4px;"></span>
          <button type="button" className="TaroMdBtn" title="链接：[文字](https://...)" onclick={() => this.insertMdWrap('[', '](https://)', '链接文字')} style={this.mdBtnStyle}>
            <i className="fas fa-link"></i>
          </button>
          <button type="button" className="TaroMdBtn" title="分隔线：---" onclick={() => this.insertAtCursor('\n---\n')} style={this.mdBtnStyle}>
            ―
          </button>
          <span style="width:1px;height:18px;background:#d2d6dc;margin:0 4px;"></span>
          {/* 语法帮助按钮 */}
          <div style="position:relative;display:inline-flex;">
            <button
              type="button"
              className="TaroMdBtn"
              title="查看 Markdown 语法速查表"
              onclick={() => { this._showHelp = !this._showHelp; m.redraw(); }}
              style={{ ...this.mdBtnStyle, color: this._showHelp ? '#4CAF50' : '#888' }}
            >
              <i className="fas fa-question-circle"></i>
            </button>
            {this._showHelp && (
              <div className="TaroMdEditor-help" style={helpStyle} onclick={() => {}}>
                <div style="font-weight:700;font-size:14px;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #e8ecf0;">
                  <i className="fas fa-markdown" style="margin-right:6px;"></i>Markdown 语法速查
                </div>
                <table style="width:100%;border-collapse:collapse;font-size:12.5px;">
                  <thead>
                    <tr style="background:#f5f7fa;">
                      <th style="padding:4px 8px;text-align:left;border-bottom:1px solid #e8ecf0;width:100px;">语法</th>
                      <th style="padding:4px 8px;text-align:left;border-bottom:1px solid #e8ecf0;">说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['**粗体**', '加粗文字'],
                      ['*斜体*', '倾斜文字'],
                      ['`代码`', '行内代码'],
                      ['## 标题', '二级标题（# 数量控制等级）'],
                      ['### 标题', '三级标题'],
                      ['> 引用', '引用区块'],
                      ['- 项目', '无序列表（也可用 * / +）'],
                      ['1. 项目', '有序列表'],
                      ['[文字](url)', '超链接'],
                      ['---', '水平分隔线'],
                      ['| 列1 | 列2 |', '表格，第二行加 |---|---|'],
                      ['~~删除~~', '删除线'],
                      ['' , ''],
                      ['' , '提示：工具栏按钮选中文字后点击即可包裹语法'],
                    ].map(([code, desc], i) =>
                      <tr style={i % 2 === 0 ? 'background:#fafbfc;' : ''}>
                        <td style="padding:3px 8px;border-bottom:1px solid #f0f0f0;font-family:Menlo,monospace;font-size:12px;color:#d63384;white-space:nowrap;">{code}</td>
                        <td style="padding:3px 8px;border-bottom:1px solid #f0f0f0;color:#555;">{desc}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 编辑区：上方 textarea，下方实时渲染预览 */}
        <div className="TaroMarkdownEditor-body">
          <textarea
            className="FormControl TaroMarkdownEditor-textarea"
            placeholder="请输入你的解读内容（支持 Markdown 语法）…"
            value={this.value}
            disabled={this.disabled}
            style={{
              width: '100%',
              minHeight: '180px',
              padding: '12px',
              border: 'none',
              borderBottom: '1px solid #e8ecf0',
              borderRadius: 0,
              resize: 'vertical',
              fontFamily: 'Menlo, Consolas, "Microsoft YaHei", monospace',
              fontSize: '14px',
              lineHeight: '1.7',
              color: '#333',
              background: '#fafbfc',
              outline: 'none',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              boxSizing: 'border-box',
            }}
            oncreate={(el) => { this._textarea = el; }}
            oninput={(e) => {
              if (this.attrs.oninput) {
                this.attrs.oninput(e.target.value);
              }
            }}
            onkeydown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
                e.preventDefault();
                this.insertMdWrap('**', '**', '粗体文字');
              }
              if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
                e.preventDefault();
                this.insertMdWrap('*', '*', '斜体文字');
              }
            }}
          />
          {/* 实时渲染预览 */}
          <div
            className="TaroMarkdownEditor-preview TaroMarkdown"
            style={{
              padding: '16px 20px',
              minHeight: '100px',
              lineHeight: '1.9',
              fontSize: '15px',
              color: '#333',
              background: '#fff',
              borderTop: this.hasContent ? '2px solid #4CAF50' : 'none',
              textAlign: 'left',
              ...(this.hasContent ? {} : { display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: '14px' }),
            }}
            oncreate={(el) => {
              if (this.hasContent) {
                setTimeout(() => this._afterRender(el.dom || el), 50);
              }
            }}
            onupdate={(el) => {
              if (this.hasContent) {
                setTimeout(() => this._afterRender(el.dom || el), 0);
              }
            }}
          >
            {this.hasContent ? (
              <Markdown text={this.value} />
            ) : (
              <span>输入内容后，渲染效果将实时显示在这里…</span>
            )}
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="TaroMarkdownEditor-actions" style="display:flex;justify-content:space-between;align-items:center;padding:8px 12px;background:#f5f7fa;border-top:1px solid #e8ecf0;">
          <small style="color:#888;">
            <i className="fas fa-info-circle"></i> 支持 Markdown · 实时渲染
            <span style="margin-left:8px;">字数：{this.value.length}</span>
          </small>
          <div style="display:flex;gap:8px;">
            {this.attrs.oncancel && (
              <button
                className="Button"
                disabled={this.loading}
                onclick={() => {
                  if (this.attrs.oncancel) this.attrs.oncancel();
                }}
              >
                取消
              </button>
            )}
            <button
              className="Button Button--primary"
              disabled={this.loading || !this.value.trim()}
              onclick={() => {
                if (this.attrs.onsubmit) this.attrs.onsubmit();
              }}
            >
              {this.loading ? '提交中...' : this.submitLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /** 工具栏按钮样式 */
  get mdBtnStyle() {
    return 'width:30px;height:30px;display:inline-flex;align-items:center;justify-content:center;border:1px solid transparent;border-radius:4px;background:transparent;color:#444;cursor:pointer;font-size:13px;';
  }

  /** 在光标位置插入纯文本 */
  insertAtCursor(text) {
    const ta = this._textarea;
    const val = this.value;
    if (!ta) {
      if (this.attrs.oninput) this.attrs.oninput(val + text);
      return;
    }
    const start = ta.selectionStart ?? val.length;
    const end = ta.selectionEnd ?? val.length;
    const before = val.substring(0, start);
    const after = val.substring(end);
    if (this.attrs.oninput) this.attrs.oninput(before + text + after);
    setTimeout(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }

  /** 在光标处包裹文本（前缀 + 选中 + 后缀） */
  insertMdWrap(prefix, suffix, placeholder) {
    const ta = this._textarea;
    const val = this.value;
    if (!ta) {
      if (this.attrs.oninput) this.attrs.oninput(val + prefix + placeholder + suffix);
      return;
    }
    const start = ta.selectionStart ?? val.length;
    const end = ta.selectionEnd ?? val.length;
    const before = val.substring(0, start);
    const selected = val.substring(start, end) || placeholder;
    const after = val.substring(end);
    if (this.attrs.oninput) this.attrs.oninput(before + prefix + selected + suffix + after);
    setTimeout(() => {
      ta.focus();
      const selStart = start + prefix.length;
      const selEnd = selStart + selected.length;
      ta.setSelectionRange(selStart, selEnd);
    }, 0);
  }

  /** 在当前行行首插入前缀 */
  insertLinePrefix(prefix) {
    const ta = this._textarea;
    const val = this.value;
    if (!ta) {
      if (this.attrs.oninput) this.attrs.oninput(prefix + val);
      return;
    }
    const pos = ta.selectionStart ?? val.length;
    let lineStart = pos;
    while (lineStart > 0 && val[lineStart - 1] !== '\n') {
      lineStart--;
    }
    const before = val.substring(0, lineStart);
    const after = val.substring(lineStart);
    if (this.attrs.oninput) this.attrs.oninput(before + prefix + after);
    setTimeout(() => {
      ta.focus();
      const newPos = pos + prefix.length;
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  }
}